import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Truck, Wallet, CreditCard } from "lucide-react";
import { TIER_CONFIG, TIER_ORDER, TIER_BENEFITS } from "../../../components/member/tierConfig";
import StatusBadge from "../../../components/shared/StatusBadge";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const MONO = "ui-monospace, SF Mono, Menlo, Fira Code, monospace";

function TierProgressBar({ tierKey, earnings }) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const index = TIER_ORDER.indexOf(tierKey);
  const current = TIER_CONFIG[tierKey];
  const nextKey = TIER_ORDER[index + 1];
  const next = nextKey ? TIER_CONFIG[nextKey] : null;

  const prevThreshold = index > 0 ? TIER_CONFIG[TIER_ORDER[index - 1]].thresholdValue : 0;
  const progressPct = next
    ? Math.min(
        100,
        Math.max(0, ((earnings - prevThreshold) / (next.thresholdValue - prevThreshold)) * 100)
      )
    : 100;
  const amountToNext = next ? Math.max(0, next.thresholdValue - earnings) : 0;

  return (
    <div style={{ flex: "1 1 280px", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", flexDirection: isMobile ? "column" : "row" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: current.color }}>
          {current.emoji} {current.label}
        </span>
        {next ? (
          <span style={{ fontSize: 12, fontWeight: 700, color: "#c3c8cc", marginTop: isMobile ? 6 : 0 }}>
            <span style={{ color: "#e8b84b" }}>
              {amountToNext.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
            </span>{" "}
            to {next.label} {next.emoji}
          </span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 700, color: "#c3c8cc" }}>Top tier reached</span>
        )}
      </div>
      <div
        style={{
          marginTop: 8,
          height: 6,
          borderRadius: 4,
          background: "rgba(255,255,255,0.06)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${progressPct}%`,
            borderRadius: 4,
            background: `linear-gradient(90deg, #00ADB5, ${current.color})`,
            transition: "width 0.4s ease",
          }}
        />
      </div>
      <p style={{ fontSize: 12, color: "#c3c8cc", margin: "8px 0 0", lineHeight: 1.5 }}>
        {TIER_BENEFITS[tierKey]}{" "}
        <span
          style={{ color: "#00ADB5", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
          onClick={() => navigate("/dashboard/my-card")}
        >
          See all tiers →
        </span>
      </p>
    </div>
  );
}

export function HeroBanner({ earningsLabel, earningsRaw, ordersCompleted, avgPayoutDays, readyToWithdraw, tierKey }) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const statsLine = [
    ordersCompleted != null ? `${ordersCompleted} orders completed` : null,
    avgPayoutDays != null ? `avg payout in ${avgPayoutDays}d` : null,
    readyToWithdraw != null ? `${readyToWithdraw} ready to withdraw` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        borderRadius: 18,
        border: "1px solid rgba(76,175,80,0.16)",
        background:
          "radial-gradient(circle at 90% 0%, rgba(0,173,181,0.1), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
        boxShadow: "0 20px 44px -26px rgba(0,0,0,0.65)",
        padding: isMobile ? 18 : 30,
      }}
    >
      <div style={{ flex: "1 1 260px", minWidth: 0, marginRight: isMobile ? 0 : 32, marginBottom: 16 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.1em", color: "#c3c8cc" }}>
          TOTAL EARNED WITH RSC
        </div>
        <div style={{ fontFamily: MONO, fontSize: isMobile ? 30 : 36, fontWeight: 800, color: "#4caf50", marginTop: 6, overflowWrap: "anywhere" }}>
          {earningsLabel}
        </div>
        {statsLine && (
          <div style={{ fontSize: 12.5, color: "#c3c8cc", marginTop: 8 }}>{statsLine}</div>
        )}
      </div>
      <TierProgressBar tierKey={tierKey} earnings={earningsRaw} />
    </div>
  );
}

function QuickActionButton({ icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "16px 16px",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.07)",
        background: "rgba(255,255,255,0.02)",
        color: disabled ? "rgba(255,255,255,0.3)" : "#fff",
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "left",
        width: "100%",
      }}
    >
      <span style={{ display: "flex", marginRight: 10 }}>{icon}</span>
      {label}
    </button>
  );
}

export function QuickActionsPanel({ walletBalanceLabel, disabled, style }) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  return (
    <div
      style={{
        flex: "1 1 260px",
        display: "flex",
        flexDirection: "column",
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.06)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        boxShadow: "0 16px 36px -26px rgba(0,0,0,0.6)",
        padding: isMobile ? 16 : 22,
        minWidth: 0,
        ...style,
      }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 800, color: "#fff", marginBottom: 16 }}>Quick actions</div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
        <QuickActionButton
          icon={<Plus size={15} color="#af58f2" />}
          label="New order commit"
          disabled={disabled}
          onClick={() => navigate("/dashboard/sell")}
        />
        <QuickActionButton
          icon={<Truck size={15} color="#00ADB5" />}
          label="Create shipment"
          disabled={disabled}
          onClick={() => navigate("/dashboard/shipment", { state: { openCreateDialog: true } })}
        />
        <QuickActionButton
          icon={<Wallet size={15} color="#4caf50" />}
          label={`Withdraw ${walletBalanceLabel}`}
          disabled={disabled}
          onClick={() => navigate("/dashboard/wallet")}
        />
        <QuickActionButton
          icon={<CreditCard size={15} color="#e8b84b" />}
          label="View my card"
          onClick={() => navigate("/dashboard/my-card")}
        />
      </div>
    </div>
  );
}

export function PendingShipmentsPanel({ shipments, total, loading, style }) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const visible = shipments.slice(0, 3);

  return (
    <div
      style={{
        flex: "1 1 320px",
        display: "flex",
        flexDirection: "column",
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,0.06)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
        boxShadow: "0 16px 36px -26px rgba(0,0,0,0.6)",
        padding: isMobile ? 16 : 22,
        minWidth: 0,
        ...style,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", marginBottom: 16 }}>
        <span style={{ fontSize: 14.5, fontWeight: 800, color: "#fff" }}>Pending shipments</span>
        {total > 0 && (
          <span style={{ fontSize: 11.5, color: "#c3c8cc", marginTop: isMobile ? 4 : 0 }}>{total} awaiting action</span>
        )}
      </div>

      <div style={{ flex: 1 }}>
        {loading ? (
          <div style={{ fontSize: 12.5, color: "#c3c8cc" }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ fontSize: 12.5, color: "#c3c8cc" }}>Nothing pending — you're all caught up.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {visible.map((s, i) => (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "11px 4px",
                  marginTop: i === 0 ? 0 : 4,
                  borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#fff" }}>{s.id}</div>
                  <div style={{ fontSize: 11.5, color: "#c3c8cc", marginTop: 2 }}>
                    {s.total_boxes ?? 0} boxes · {s.total_items ?? 0} units
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate("/dashboard/shipment")}
        style={{
          display: "flex",
          alignItems: "center",
          background: "transparent",
          border: "none",
          color: "#00ADB5",
          fontSize: 12.5,
          fontWeight: 700,
          cursor: "pointer",
          padding: "14px 4px 0",
          marginTop: "auto",
        }}
      >
        View all shipments
        <ArrowRight size={12} style={{ marginLeft: 5 }} />
      </button>
    </div>
  );
}
